import React, { useState, useRef, useEffect } from 'react';

function FieldRenderer({ field, value, onChange, error, stores, readOnly = false }) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleChange = (e) => {
    const newValue = e.target.type === 'checkbox'
      ? e.target.checked
      : e.target.value;
    onChange(field.field_code, newValue);
  };

  const handleMultiSelectToggle = (optionValue) => {
    const currentValues = Array.isArray(value) ? value : [];
    const newValues = currentValues.includes(optionValue)
      ? currentValues.filter(v => v !== optionValue)
      : [...currentValues, optionValue];
    onChange(field.field_code, newValues);
  };

  const getMultiSelectLabel = () => {
    if (!value || value.length === 0) {
      return `Select ${field.field_name}`;
    }
    const selectedOptions = field.field_options.filter(opt =>
      value.includes(opt.value || opt)
    );
    return selectedOptions.map(opt => opt.label || opt).join(', ');
  };

  const renderInput = () => {
    switch (field.field_type) {
      case 'text':
        return (
          <input
            type="text"
            value={value || ''}
            onChange={handleChange}
            placeholder={field.placeholder_text || ''}
            required={field.is_required}
            className="field-input"
            disabled={readOnly}
          />
        );

      case 'textarea':
        return (
          <textarea
            value={value || ''}
            onChange={handleChange}
            placeholder={field.placeholder_text || ''}
            required={field.is_required}
            rows="4"
            className="field-input"
            disabled={readOnly}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={value || ''}
            onChange={handleChange}
            placeholder={field.placeholder_text || ''}
            required={field.is_required}
            className="field-input"
            min={field.validation_rules?.min}
            max={field.validation_rules?.max}
            disabled={readOnly}
          />
        );

      case 'currency':
        return (
          <div className="currency-input-wrapper">
            <span className="currency-symbol">$</span>
            <input
              type="number"
              value={value || ''}
              onChange={handleChange}
              placeholder={field.placeholder_text || '0.00'}
              required={field.is_required}
              className="field-input currency-input"
              min="0"
              step="0.01"
              disabled={readOnly}
            />
          </div>
        );

      case 'date':
        return (
          <input
            type="date"
            value={value || ''}
            onChange={handleChange}
            required={field.is_required}
            className="field-input"
            disabled={readOnly}
          />
        );

      case 'dropdown':
        // Check if this dropdown should use API-sourced stores
        if (field.field_source === 'api' && stores) {
          return (
            <select
              value={value || ''}
              onChange={handleChange}
              required={field.is_required}
              className="field-input"
              disabled={readOnly}
            >
              <option value="">Select {field.field_name}</option>
              {[...stores]
                .sort((a, b) => a.store_name.localeCompare(b.store_name))
                .map((store) => (
                  <option key={store.store_pc} value={store.store_pc}>
                    {store.store_name}
                  </option>
                ))
              }
            </select>
          );
        }

        // Otherwise use static field_options
        return (
          <select
            value={value || ''}
            onChange={handleChange}
            required={field.is_required}
            className="field-input"
            disabled={readOnly}
          >
            <option value="">Select {field.field_name}</option>
            {field.field_options && field.field_options.map((option, idx) => {
              const optionValue = option.value || option;
              const optionLabel = option.label || option;
              return (
                <option key={idx} value={optionValue}>
                  {optionLabel}
                </option>
              );
            })}
          </select>
        );

      case 'multi_select':
        return (
          <div className="multi-select-dropdown" ref={dropdownRef}>
            <button
              type="button"
              className="multi-select-trigger"
              onClick={() => !readOnly && setIsDropdownOpen(!isDropdownOpen)}
              disabled={readOnly}
            >
              <span className="multi-select-label">{getMultiSelectLabel()}</span>
              <span className="multi-select-arrow">{isDropdownOpen ? '▲' : '▼'}</span>
            </button>

            {isDropdownOpen && (
              <div className="multi-select-options">
                {field.field_options && field.field_options.map((option, idx) => {
                  const optionValue = option.value || option;
                  const optionLabel = option.label || option;
                  const isSelected = Array.isArray(value) && value.includes(optionValue);

                  return (
                    <label key={idx} className="multi-select-option">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleMultiSelectToggle(optionValue)}
                        className="multi-select-checkbox"
                      />
                      <span className="multi-select-option-label">{optionLabel}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        );

      case 'checkbox':
        return (
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={value || false}
              onChange={handleChange}
              required={field.is_required}
              className="field-checkbox"
              disabled={readOnly}
            />
            <span>{field.placeholder_text || 'Yes'}</span>
          </label>
        );

      default:
        return (
          <input
            type="text"
            value={value || ''}
            onChange={handleChange}
            placeholder={field.placeholder_text || ''}
            required={field.is_required}
            className="field-input"
            disabled={readOnly}
          />
        );
    }
  };

  return (
    <div className="conditional-field-group">
      <label className="field-label">
        {field.field_name}
        {field.is_required && <span className="field-required">*</span>}
      </label>

      {renderInput()}

      {field.help_text && (
        <span className="field-help-text">{field.help_text}</span>
      )}

      {error && (
        <span className="field-error">{error}</span>
      )}
    </div>
  );
}

export default FieldRenderer;